import { forwardRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { getFlagState } from "@/lib/flags";

const CertificateRender = forwardRef(({ cert, verifyUrl }, ref) => {
  const fs = getFlagState(cert.flag_state);
  if (fs.letterhead) {
    return (
      <LetterheadCertificate ref={ref} cert={cert} verifyUrl={verifyUrl} flag={fs} />
    );
  }
  return <ClassicCertificate ref={ref} cert={cert} verifyUrl={verifyUrl} flag={fs} />;
});

CertificateRender.displayName = "CertificateRender";
export default CertificateRender;

const ENDORSEMENT_TITLES = {
  "CoC Endorsement": "CERTIFICATE OF RECOGNITION OF ENDORSEMENT",
  "GMDSS General Operator": "GMDSS GENERAL OPERATOR ENDORSEMENT",
  "Ship Security Officer (SSO)": "SHIP SECURITY OFFICER (SSO) ENDORSEMENT",
  "Certificate of Proficiency": "CERTIFICATE OF PROFICIENCY",
};

function getTitle(cert) {
  return ENDORSEMENT_TITLES[cert.endorsement_type] || ENDORSEMENT_TITLES["CoC Endorsement"];
}

const cellStyle = {
  border: "1.5px solid #000",
  padding: "5px 8px",
  textAlign: "left",
  verticalAlign: "middle",
  fontWeight: 700,
  color: "#000",
};

function cellHeaderStyle(width) {
  return {
    border: "1.5px solid #000",
    padding: "5px 8px",
    textAlign: "left",
    verticalAlign: "middle",
    fontWeight: 700,
    color: "#000",
    background: "#f0f0f0",
    textTransform: "uppercase",
    width: `${width}%`,
  };
}

/* ------------------------------------------------------------------ */
/* Letterhead-based certificate (Zambia, SKAN, etc.)                  */
/* ------------------------------------------------------------------ */
const LetterheadCertificate = forwardRef(({ cert, verifyUrl, flag }, ref) => {
  return (
    <div
      ref={ref}
      className="bg-white text-[#0a0a0a] mx-auto relative"
      style={{
        width: "100%",
        maxWidth: "900px",
        aspectRatio: "1 / 1.414",
        backgroundImage: `url(${flag.letterhead})`,
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
        fontFamily: "'Times New Roman', Times, serif",
      }}
      data-testid="certificate-render"
    >
      {/* Body overlay positioned in the middle white area of the letterhead */}
      <div
        className="absolute"
        style={{
          left: "6%",
          right: "6%",
          top: "16%",
          bottom: flag.hasBuiltInFooter ? "22%" : "12%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Title row */}
        <div className="text-center mb-2">
          <div className="text-[12px] font-bold uppercase tracking-wide">
            {getTitle(cert)}
          </div>
          <div className="text-[10px] mt-0.5 px-4 leading-tight">
            ISSUED UNDER THE PROVISIONS OF THE INTERNATIONAL CONVENTION ON THE
            STANDARDS OF TRAINING, CERTIFICATION AND WATCHKEEPING FOR SEAFARERS
            1978, AS AMENDED IN 2010
          </div>
        </div>

        <div className="text-[12px] mb-1">
          <span className="font-bold">COC: </span>
          <span className="font-bold text-[#a82828] text-[14px] tracking-wide">
            {cert.coc_number}
          </span>
        </div>

        <div className="text-[11px] leading-[1.45] text-justify mb-2">
          The Government of {flag.name} certifies that{" "}
          <span className="font-bold underline">{cert.holder_name}</span>{" "}
          Holding Certificate No:{" "}
          <span className="font-bold underline">{cert.holding_certificate_no}</span>{" "}
          Issued by{" "}
          <span className="font-bold underline">{cert.issued_by_country}</span>{" "}
          Rank <span className="font-bold underline">{cert.rank}</span>{" "}
          is duly Recognized in accordance with the provisions of regulation{" "}
          <span className="font-bold underline">{cert.regulation}</span> of the
          above convention, as amended, and has been found competent to perform
          the following functions, at the level specified, subject to any
          limitations indicated until{" "}
          <span className="font-bold underline">{cert.valid_until}</span>
        </div>

        {(cert.functions?.length || 0) > 0 && (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1.5px solid #000",
              fontSize: "10px",
              marginBottom: "8px",
              fontFamily: "'Times New Roman', Times, serif",
              background: "#fff",
            }}
          >
            <thead>
              <tr>
                <th style={cellHeaderStyle(55)}>FUNCTION</th>
                <th style={cellHeaderStyle(22)}>LEVEL</th>
                <th style={cellHeaderStyle(23)}>LIMITATIONS</th>
              </tr>
            </thead>
            <tbody>
              {cert.functions.map((f, i) => (
                <tr key={i}>
                  <td style={cellStyle}>{f.function}</td>
                  <td style={cellStyle}>{f.level}</td>
                  <td style={cellStyle}>{f.limitations || "NONE"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {(cert.capacities?.length || 0) > 0 && (
          <>
            <div className="text-[10px] leading-snug mb-1">
              The lawful holder may serve in the following capacity or
              capacities, subject to safe-manning requirements of the
              Administration:
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1.5px solid #000",
                fontSize: "10px",
                marginBottom: "8px",
                fontFamily: "'Times New Roman', Times, serif",
                background: "#fff",
              }}
            >
              <thead>
                <tr>
                  <th style={cellHeaderStyle(40)}>CAPACITY</th>
                  <th style={cellHeaderStyle(60)}>LIMITATION (IF ANY)</th>
                </tr>
              </thead>
              <tbody>
                {cert.capacities.map((c, i) => (
                  <tr key={i}>
                    <td style={cellStyle}>{c.capacity}</td>
                    <td style={cellStyle}>{c.limitation || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Bottom row: details on left + photo & signature grouped at right */}
        <div className="mt-auto flex gap-4 items-end">
          <div className="flex-1 text-[10px] leading-tight">
            <div>
              <span className="font-semibold">Cert. of Equivalent No: </span>
              <span className="underline font-bold">{cert.cert_equivalent_no}</span>
            </div>
            <div>
              <span className="font-semibold">Issued On: </span>
              <span className="underline font-bold">{cert.issued_on}</span>
              <span className="font-semibold ml-3">Place of Issue: </span>
              <span className="underline font-bold">{cert.place_of_issue}</span>
            </div>
            {cert.coc_issue_date && (
              <div>
                <span className="font-semibold">COC Issue Date: </span>
                <span className="underline font-bold">{cert.coc_issue_date}</span>
                <span className="font-semibold ml-3">COC Valid Until: </span>
                <span className="underline font-bold">{cert.valid_until}</span>
              </div>
            )}
            <div>
              <span className="font-semibold">Date of Birth: </span>
              <span className="underline font-bold">{cert.date_of_birth}</span>
              <span className="font-semibold ml-3">Nationality: </span>
              <span className="underline font-bold">{cert.nationality}</span>
            </div>

            {!flag.hasBuiltInFooter && (
              <div className="mt-3">
                <div
                  className="cert-signature mb-1"
                  style={{ fontSize: "20px" }}
                >
                  R. Williams
                </div>
                <div className="border-t border-black w-48" />
                <div className="text-[9px] font-semibold">
                  Name & Signature of Duly Authorised Official
                </div>
              </div>
            )}
          </div>

          {/* Photo + signature grouped together on RIGHT */}
          <div className="flex-shrink-0 flex flex-col items-center gap-2">
            {cert.photo_base64 ? (
              <img
                src={cert.photo_base64}
                alt="holder"
                style={{
                  width: "95px",
                  height: "115px",
                  objectFit: "cover",
                  border: "1px solid #000",
                  background: "#fff",
                }}
              />
            ) : (
              <div
                style={{
                  width: "95px",
                  height: "115px",
                  border: "1px dashed #555",
                  background: "#fff",
                }}
                className="flex items-center justify-center text-[8px] text-stone-500"
              >
                No photo
              </div>
            )}
            <SignatureBlock cert={cert} />
          </div>
        </div>
      </div>

      {/* QR code at the BOTTOM-LEFT */}
      <div
        className="absolute"
        style={{
          left: "6%",
          bottom: "3%",
          zIndex: 5,
          background: "#fff",
          padding: "2px",
          border: "1px solid rgba(0,0,0,0.2)",
        }}
      >
        <QRCodeSVG value={verifyUrl} size={60} level="M" />
        <div className="text-[7px] text-center leading-tight mt-0.5">
          Scan to verify
        </div>
      </div>
    </div>
  );
});
LetterheadCertificate.displayName = "LetterheadCertificate";

/* ------------------------------------------------------------------ */
/* Classic certificate (used when no letterhead is configured)        */
/* ------------------------------------------------------------------ */
const ClassicCertificate = forwardRef(({ cert, verifyUrl, flag }, ref) => {
  const watermarkText = `${flag.name} Maritime Administration `.repeat(20);

  return (
    <div
      ref={ref}
      className="cert-paper bg-white text-[#0a0a0a] mx-auto relative"
      style={{
        width: "100%",
        maxWidth: "900px",
        aspectRatio: "1 / 1.414",
        padding: "26px",
        fontFamily: "'Times New Roman', Times, serif",
      }}
      data-testid="certificate-render"
    >
      <div className="cert-watermark">
        {Array.from({ length: 60 }).map((_, i) => (
          <div key={i} className="row">
            {watermarkText}
          </div>
        ))}
      </div>

      <div className="cert-content cert-border-outer">
        <div className="cert-border-inner relative">
          {/* Photo TOP-RIGHT */}
          <div
            className="absolute"
            style={{ top: "8px", right: "8px", zIndex: 5 }}
          >
            {cert.photo_base64 ? (
              <img
                src={cert.photo_base64}
                alt="holder"
                style={{
                  width: "100px",
                  height: "120px",
                  objectFit: "cover",
                  border: "1px solid #000",
                  background: "#fff",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100px",
                  height: "120px",
                  border: "1px dashed #555",
                  background: "#fff",
                }}
                className="flex items-center justify-center text-[10px] text-stone-500"
              >
                No photo
              </div>
            )}
          </div>

          <div className="text-center mb-3" style={{ paddingRight: "120px" }}>
            <div className="text-[15px] font-semibold">{flag.name}</div>
            <div className="flex justify-center my-1">
              {flag.emblem && (
                <img
                  src={flag.emblem}
                  alt={`${flag.name} emblem`}
                  crossOrigin="anonymous"
                  style={{ width: "78px", height: "78px", objectFit: "contain" }}
                />
              )}
            </div>
            <div className="text-[14px]">Maritime Administration</div>
            <div className="text-[20px] font-bold mt-1 leading-tight">
              {flag.authority}
            </div>
            <div className="text-[11px] leading-tight mt-1 px-6">
              {getTitle(cert)} — ISSUED UNDER THE PROVISIONS OF THE
              <br />
              INTERNATIONAL CONVENTION ON THE STANDARDS OF TRAINING,
              <br />
              CERTIFICATE AND WATCHKEEPING FOR SEAFARERS 1978, AS AMENDED IN 2010
            </div>
          </div>

          <div className="text-[14px] mb-2">
            <span className="font-bold">COC: </span>
            <span className="font-bold text-[#a82828] text-[16px] tracking-wide">
              {cert.coc_number}
            </span>
          </div>

          <div className="text-[12.5px] leading-[1.55] text-justify mb-3">
            The Government of {flag.name} certifies that{" "}
            <span className="font-bold underline">{cert.holder_name}</span>{" "}
            Holding Certificate No:{" "}
            <span className="font-bold underline">{cert.holding_certificate_no}</span>{" "}
            Issued by{" "}
            <span className="font-bold underline">{cert.issued_by_country}</span>{" "}
            Rank <span className="font-bold underline">{cert.rank}</span>{" "}
            is duly Recognized in accordance with the provisions of regulation{" "}
            <span className="font-bold underline">{cert.regulation}</span> of
            the above convention, as amended, and has been found competent to
            perform the following functions, at the level specified, subject to
            any limitations indicated until{" "}
            <span className="font-bold underline">{cert.valid_until}</span>
          </div>

          {(cert.functions?.length || 0) > 0 && (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1.5px solid #000",
                fontSize: "12px",
                marginBottom: "12px",
                fontFamily: "'Times New Roman', Times, serif",
                background: "#fff",
              }}
            >
              <thead>
                <tr>
                  <th style={cellHeaderStyle(50)}>FUNCTION</th>
                  <th style={cellHeaderStyle(25)}>LEVEL</th>
                  <th style={cellHeaderStyle(25)}>LIMITATIONS (IF ANY)</th>
                </tr>
              </thead>
              <tbody>
                {cert.functions.map((f, i) => (
                  <tr key={i}>
                    <td style={cellStyle}>{f.function}</td>
                    <td style={cellStyle}>{f.level}</td>
                    <td style={cellStyle}>{f.limitations || "NONE"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {(cert.capacities?.length || 0) > 0 && (
            <>
              <div className="text-[12px] leading-snug mb-1">
                The lawful holder of this certificate may serve in the
                following capacity or capacities specified in the applicable
                safe manning requirements of the Administration:
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  border: "1.5px solid #000",
                  fontSize: "12px",
                  marginBottom: "12px",
                  fontFamily: "'Times New Roman', Times, serif",
                  background: "#fff",
                }}
              >
                <thead>
                  <tr>
                    <th style={cellHeaderStyle(40)}>CAPACITY</th>
                    <th style={cellHeaderStyle(60)}>LIMITATION (IF ANY)</th>
                  </tr>
                </thead>
                <tbody>
                  {cert.capacities.map((c, i) => (
                    <tr key={i}>
                      <td style={cellStyle}>{c.capacity}</td>
                      <td style={cellStyle}>{c.limitation || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="text-[12.5px] mb-1">
            <span className="font-semibold">Certificate of Equivalent No: </span>
            <span className="underline font-bold">{cert.cert_equivalent_no}</span>
          </div>
          <div className="text-[12.5px] mb-2 flex flex-wrap gap-x-8">
            <div>
              <span className="font-semibold">Issued On: </span>
              <span className="underline font-bold">{cert.issued_on}</span>
            </div>
            <div>
              <span className="font-semibold">Place of Issue: </span>
              <span className="underline font-bold">{cert.place_of_issue}</span>
            </div>
          </div>

          <div className="relative flex items-end justify-between mt-4 min-h-[150px]">
            <div className="flex-1 pr-2">
              <div className="cert-signature mb-1">R. Williams</div>
              <div className="border-t border-black w-60" />
              <div className="text-[11px] leading-tight">
                <div className="font-semibold">
                  Name & Signature of the Duly Authorised Official
                </div>
                <div className="italic mt-1">
                  The original of this certificate must be kept available in
                  accordance with
                  <br />
                  Regulation I/2, Paragraph 11 of the convention while serving
                  on a ship
                </div>
              </div>

              <div className="mt-4">
                <SignatureBlock cert={cert} />
              </div>

              <div className="text-[12px] mt-2 leading-snug">
                <div>
                  <span className="font-semibold">
                    Date of birth of the holder of the certificate:{" "}
                  </span>
                  <span className="underline font-bold">{cert.date_of_birth}</span>
                </div>
                <div>
                  <span className="font-semibold">
                    Nationality of the holder of the certificate:{" "}
                  </span>
                  <span className="underline font-bold">{cert.nationality}</span>
                </div>
              </div>
            </div>

            <div
              className="cert-stamp"
              style={{ right: "20px", bottom: "30px", position: "absolute" }}
            >
              <div>
                {flag.name}
                <br />
                MARITIME
                <br />
                ADMINISTRATION
                <br />
                ★ OFFICIAL ★
              </div>
            </div>
          </div>

          {/* QR at the BOTTOM-LEFT inside footer */}
          <div className="mt-3 pt-2 border-t border-[#0a4080] flex items-center justify-between">
            <div className="bg-white p-1 border border-black/30">
              <QRCodeSVG value={verifyUrl} size={60} level="M" />
            </div>
            <div className="text-[11px] text-[#0a4080]">
              For authentication of the certificate: {flag.domain}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
ClassicCertificate.displayName = "ClassicCertificate";

/* ------------------------------------------------------------------ */
function SignatureBlock({ cert }) {
  return (
    <div>
      {cert.signature_base64 ? (
        <img
          src={cert.signature_base64}
          alt="holder signature"
          style={{ height: "36px", maxWidth: "200px", objectFit: "contain" }}
        />
      ) : (
        <div style={{ height: "30px" }} />
      )}
      <div className="border-t border-black w-48" />
      <div className="text-[10px] font-semibold">
        Signature of the holder of the certificate
      </div>
    </div>
  );
}
